import React, { useState, useEffect } from "react";
import "./CancelSubscriptionDialog.css";

const API_URL = process.env.REACT_APP_API_URL;

const RETENTION_MESSAGES = [
  "People who used Brandly saw their audience grew by 4X in first 30 days",
  "People who used Brandly were able to close 3X more business clients",
  "People who use Brandly save 4 hours weekly on Content creation",
  "People who use Brandly have seen their brand grow by 3X in first 30 days",
  "Brandly users report 300% increase in LinkedIn engagement",
  "Our users have generated over 10,000 viral posts this month",
  "Brandly helped users increase their professional network by 500%",
  "Users save an average of 20 hours per week with our content tools"
];

const CANCELLATION_REASONS = [
  "Too expensive for me",
  "It's hard to use",
  "Growing on LinkedIn is not my priority",
  "Others"
];

const CancelSubscriptionDialog = ({ isOpen, onClose, onCancel, currentPlan }) => {
  const [step, setStep] = useState(1); // 1: retention messages, 2: free month offer, 3: feedback form
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setCurrentMessageIndex(0);
      setSelectedReason("");
      setOtherReason("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 1 && isOpen) {
      const interval = setInterval(() => {
        setCurrentMessageIndex((prev) => 
          prev === RETENTION_MESSAGES.length - 1 ? 0 : prev + 1
        );
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [step, isOpen]);

  const handleContinueToCancel = () => {
    setStep(2);
  };

  const handleEnjoyForFree = async () => {
    // This would typically extend the subscription for a free month
    // For now, we'll just close the dialog
    alert("Thank you! We've extended your subscription for one month free. Enjoy Brandly!");
    onClose();
  };

  const handleContinueToCancelFinal = () => {
    setStep(3);
  };

  const handleSubmitCancellation = async () => {
    if (!selectedReason) {
      alert("Please select a reason for cancellation");
      return;
    }

    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          cancel_at_next_billing: false,
          feedback_reason: selectedReason,
          feedback_other: selectedReason === "Others" ? otherReason : null,
        }),
      });

      if (response.ok) {
        alert("Your subscription has been cancelled successfully. We're sorry to see you go!");
        onClose();
        // Refresh the page to update the UI
        window.location.reload();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || "Failed to cancel subscription");
      }
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cancel-dialog-overlay">
      <div className="cancel-dialog">
        {step === 1 && (
          <div className="retention-step">
            <div className="dialog-header">
              <h2>We're sorry to see you go!</h2>
              <p>Before you cancel, here's what our users are saying:</p>
            </div>
            
            <div className="retention-message">
              <div className="message-card">
                <p>"{RETENTION_MESSAGES[currentMessageIndex]}"</p>
              </div>
            </div>

            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Back to Account
              </button>
              <button className="btn btn-primary" onClick={handleContinueToCancel}>
                Continue to Cancel
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="free-month-step">
            <div className="dialog-header">
              <h2>Wait! Don't go yet!</h2>
              <p>We'd love to keep you as part of the Brandly family. How about we give you one month completely free to see if we can change your mind?</p>
            </div>

            <div className="offer-card">
              <h3>🎉 One Month Free on Us!</h3>
              <p>Continue with Brandly and enjoy all premium features for free for the next month. No strings attached!</p>
            </div>

            <div className="dialog-actions">
              <button className="btn btn-success" onClick={handleEnjoyForFree}>
                Enjoy For Free
              </button>
              <button className="btn btn-danger" onClick={handleContinueToCancelFinal}>
                Continue to Cancel
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="feedback-step">
            <div className="dialog-header">
              <h2>Help us improve</h2>
              <p>We'd love to know how we could have made things better for you:</p>
            </div>

            <div className="feedback-form">
              <div className="reason-options">
                {CANCELLATION_REASONS.map((reason) => (
                  <label key={reason} className="reason-option">
                    <input
                      type="radio"
                      name="reason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={(e) => setSelectedReason(e.target.value)}
                    />
                    <span>{reason}</span>
                  </label>
                ))}
              </div>

              {selectedReason === "Others" && (
                <div className="other-reason">
                  <textarea
                    placeholder="Please tell us more..."
                    value={otherReason}
                    onChange={(e) => setOtherReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>

            <div className="dialog-actions">
              <button className="btn btn-secondary" onClick={onClose}>
                Back to Account
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleSubmitCancellation}
                disabled={cancelling}
              >
                {cancelling ? "Cancelling..." : "Confirm Cancellation"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancelSubscriptionDialog;
